// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="DestructiveConfirm" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { useMediaQuery } from '../../hooks/use-media-query';
import { cn } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './drawer';

// ─── DestructiveConfirmContext ────────────────────────────────────────────────

interface DestructiveConfirmContextValue {
  /** When true, DestructiveConfirm bypasses the dialog and fires onConfirm immediately. */
  skipConfirm: boolean;
}

const DestructiveConfirmContext = React.createContext<DestructiveConfirmContextValue>({
  skipConfirm: false,
});

export function DestructiveConfirmProvider({
  skipConfirm,
  children,
}: {
  skipConfirm: boolean;
  children: React.ReactNode;
}) {
  return (
    <DestructiveConfirmContext.Provider value={{ skipConfirm }}>
      {children}
    </DestructiveConfirmContext.Provider>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DestructiveConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  isPending?: boolean;
}

// ─── Shared action button ─────────────────────────────────────────────────────

const actionBaseClass = cn(
  'inline-flex w-full items-center justify-center rounded-[var(--radius)] px-4 text-sm font-medium',
  'h-10 sm:h-9 sm:w-auto',
  'transition-colors',
  'bg-destructive text-white',
  'hover:bg-[var(--color-destructive-hover,color-mix(in_oklch,var(--color-destructive)_90%,black))]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:pointer-events-none disabled:opacity-50',
);

const cancelBaseClass = cn(
  'inline-flex w-full items-center justify-center rounded-[var(--radius)] px-4 text-sm font-medium',
  'h-10 sm:h-9 sm:w-auto',
  'transition-colors',
  'border border-border bg-background text-foreground',
  'hover:bg-paper',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:pointer-events-none disabled:opacity-50',
);

// ─── DestructiveConfirm ───────────────────────────────────────────────────────

/**
 * Responsive destructive-action confirmation:
 * - Mobile (< 640 px): bottom-anchored Drawer (iOS HIG — Action Sheet pattern)
 * - Desktop (≥ 640 px): centred AlertDialog
 *
 * Both surfaces share identical copy and action semantics.
 * The confirm button always uses `bg-destructive text-white` for contrast.
 *
 * @example
 * ```tsx
 * <DestructiveConfirm
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Delete tenant?"
 *   description="This cannot be undone. All data for this tenant will be permanently removed."
 *   confirmLabel="Delete"
 *   onConfirm={handleDelete}
 *   isPending={deleteUser.isPending}
 * />
 * ```
 */
const DestructiveConfirm = React.forwardRef<HTMLDivElement, DestructiveConfirmProps>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      confirmLabel = 'Delete',
      cancelLabel = 'Cancel',
      onConfirm,
      isPending = false,
    },
    _ref,
  ) => {
    const { skipConfirm } = React.useContext(DestructiveConfirmContext);
    const isMobile = useMediaQuery('(max-width: 639px)');

    React.useEffect(() => {
      if (skipConfirm && open) {
        onConfirm();
        onOpenChange(false);
      }
    }, [skipConfirm, open, onConfirm, onOpenChange]);

    if (skipConfirm) return null;

    if (isMobile) {
      return (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent>
            <DrawerHeader className="text-left px-4 pt-4 pb-2">
              <DrawerTitle>{title}</DrawerTitle>
              {description && <DrawerDescription>{description}</DrawerDescription>}
            </DrawerHeader>
            <DrawerFooter className="px-4 pb-6 gap-2">
              <button
                type="button"
                className={actionBaseClass}
                onClick={onConfirm}
                disabled={isPending}
              >
                {confirmLabel}
              </button>
              <DrawerClose asChild>
                <button type="button" className={cancelBaseClass} disabled={isPending}>
                  {cancelLabel}
                </button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      );
    }

    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>{cancelLabel}</AlertDialogCancel>
            <button
              type="button"
              className={actionBaseClass}
              onClick={onConfirm}
              disabled={isPending}
            >
              {confirmLabel}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);
DestructiveConfirm.displayName = 'DestructiveConfirm';

export { DestructiveConfirm };
